import axios from "axios";

import { apiMethods } from "./endpoints";

export class ApiClient {
  constructor(apiBase) {
    this.apiBase = apiBase;
    this.token = null;
  }

  /**
   * @param {string|null} token
   */
  setJwtToken(token) {
    this.token = token;
  }

  /**
   * @param {string} apiBase
   */
  setApiBase(apiBase) {
    this.apiBase = apiBase;
  }

  /**
   * Injects API methods from config
   * @param {*} metadata
   */
  injectApiMethods(metadata) {
    for (const name of Object.keys(metadata)) {
      this[name] = async (...args) => {
        const {
          method,
          url,
          data,
          headers,
          transform,
          custom,
          options,
        } = await metadata[name](...args);

        if (custom) {
          return await custom(...args);
        }
        let result = await this.request(method, url, data, headers, options);

        if (transform && typeof transform === "function") {
          result = await transform(result);
        }

        return result;
      };
    }

    return this;
  }

  /**
   * Make a request to API
   * @param {string} method
   * @param {string} url
   * @param {object} options
   * @param {*} data
   */
  async request(method, url, data, headers = {}, options = {}) {
    const definedHeaders = this.headers();

    const params = {
      method,
      headers: { ...definedHeaders, ...headers },
      url,
      baseURL: this.apiBase,
      [this.dataSendType(method)]: this.serialize(
        data,
        method,
        options.keepItRaw
      ),
      ...options,
    };

    const response = await axios.request(params);

    return response.data;
  }

  serialize(data, method, keepItRaw) {
    if (keepItRaw) {
      return data;
    }
    return data && ["POST", "PUT"].includes(method.toUpperCase())
      ? JSON.stringify(data)
      : data;
  }

  /**
   * @param {string} method
   * @returns {string}
   */
  dataSendType(method) {
    return method === "GET" ? "params" : "data";
  }

  /**
   * Build request headers
   */
  headers() {
    const headers = ApiClient.STATIC_HEADERS;

    if (this.token) {
      headers.authorization = `Bearer ${this.token}`;
    }

    return headers;
  }

  addErrorInterceptor(interceptorFunc) {
    axios.interceptors.response.use((r) => r, interceptorFunc);
  }

  /**
   * @param {*} args
   */
  static create(...args) {
    const client = new this(...args);

    return client.injectApiMethods(apiMethods);
  }

  /**
   * Static headers
   */
  static get STATIC_HEADERS() {
    return {
      "Content-Type": "application/json",
    };
  }
}
